<script lang="ts">
	import WarsawImage from '$lib/assets/page/about/lazienki_park_warsaw.avif';
	import MexicoCityImage from '$lib/assets/page/about/palace_fine_arts_mexico_city.avif';
	import RomeImage from '$lib/assets/page/about/skyline_rome_italy.avif';
	import Attribution from '$lib/components/ui/Attribution.svelte';
	import SmallCard from '$lib/components/ui/SmallCard.svelte';
	import { FULL_SITE_URL, SITE_NAME } from '$lib/statics';
	import { UrlUtil } from '$lib/utils/url.util';
	import { type Component } from 'svelte';
	import IconBuilding from '~icons/mdi/building';
	import IconGreenCircle from '~icons/mdi/green-circle';
	import IconHourglass from '~icons/mdi/hourglass';

	const philosophies: { Icon: Component; title: string; desc: string }[] = [
		{
			Icon: IconBuilding,
			title: 'Beyond the Top 10',
			desc: 'We were tired of generic lists that told us where to stand but never what we were looking at. Velatlas creates a connection between the traveler and the destination.'
		},
		{
			Icon: IconHourglass,
			title: 'Context is Everything',
			desc: 'A skyline is just a view until you know the history behind it. Our unique Timeline Events and deep-dive Stories turn sightseeing into time travel.'
		},
		{
			Icon: IconGreenCircle,
			title: 'Curated Beauty',
			desc: 'From hidden green spaces to architectural marvels, we curate spots that offer genuine beauty and atmosphere, not just tourist footfall.'
		}
	];

	const meta = {
		title: `About ${SITE_NAME} - Travel Deeper`,
		description: `${SITE_NAME} moves beyond generic top 10 lists. We combine curated travel planning with deep historical context, stories, and timelines for the modern explorer.`,
		canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toAbout().href}`,
		imageUrl: UrlUtil.getStaticFileFullUrl('images', 'scenic-coastal-city-travel-destinations-brazil-banner.jpg')
	};
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />
	<meta name="robots" content="index, follow" />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />
</svelte:head>

<div class="flex min-h-screen flex-col gap-10 overflow-x-hidden bg-base-100 pt-16 text-base-content">
	<section class="relative container mx-auto mt-12 px-4 md:mt-24">
		{@render Hero()}
	</section>

	<section class="bg-base-200/50 py-10">
		{@render OriginStory()}
	</section>

	<section class="container mx-auto py-5">
		{@render Discovery()}
	</section>

	<section class="container mx-auto py-5">
		{@render Disclaimer()}
	</section>

	<section class="relative flex h-[60vh] w-full items-center justify-center overflow-hidden">
		{@render VisualManifesto()}
	</section>
</div>

{#snippet Hero()}
	<header class="mx-auto max-w-5xl text-center">
		<h1 class="mb-8 text-6xl md:text-8xl">
			Don't just visit.<br />
			<span class="text-primary"> Understand. </span>
		</h1>
		<div class="mx-auto mb-8 h-1 w-24 rounded-full bg-base-content/10"></div>
		<p class="mx-auto max-w-3xl text-xl leading-relaxed font-light text-base-content/70 md:text-2xl">
			We are building the definitive resource for the modern traveler, combining the utility of a smart planner with the rich
			context you need to truly appreciate every destination.
		</p>
	</header>
{/snippet}

{#snippet OriginStory()}
	<div class="container mx-auto px-4">
		<div class="flex flex-col items-center gap-16 lg:flex-row">
			<div class="relative lg:w-1/2">
				<div class="grid grid-cols-2 gap-4">
					<div class="group relative z-0 translate-y-8 transform">
						<figure class="aspect-4/5 w-full overflow-hidden rounded-3xl shadow-2xl">
							<img
								class="h-full w-full object-cover"
								src={MexicoCityImage}
								alt="Palace of Fine Arts in Mexico City, Mexico"
								width="828"
								height="552"
								draggable="false"
							/>
						</figure>

						<div class="absolute bottom-3 left-3 z-100">
							<Attribution
								horizontalAlign="dropdown-start"
								openFrom="dropdown-bottom"
								attribution={{
									author: 'Cohete_verde',
									sourceUrl: 'https://en.wikipedia.org/wiki/File:Bellas_Artes_01.jpg',
									licence: { name: 'CC BY-SA 3.0', link: 'https://creativecommons.org/licenses/by-sa/3.0/deed.en' }
								}}
							/>
						</div>
					</div>

					<div class="group relative z-0 -translate-y-8 transform">
						<figure class="aspect-4/5 w-full overflow-hidden rounded-3xl shadow-2xl">
							<img
								class="h-full w-full object-cover"
								src={WarsawImage}
								alt="Autumn in Łazienki Park in Warsaw, Poland"
								width="774"
								height="518"
								draggable="false"
							/>
						</figure>

						<div class="absolute bottom-3 left-3 z-30">
							<Attribution
								horizontalAlign="dropdown-center"
								openFrom="dropdown-bottom"
								attribution={{
									author: 'Voyager747',
									sourceUrl: 'https://en.wikipedia.org/wiki/File:Pa%C5%82ac_na_Wodzie,%C5%81azienki_Kr%C3%B3lewskie,Warszawa.jpg',
									licence: { name: 'CC BY 3.0', link: 'https://creativecommons.org/licenses/by/3.0/deed.en' }
								}}
							/>
						</div>
					</div>
				</div>
				<div class="absolute inset-0 -z-10 rounded-full bg-primary/20 opacity-50 blur-3xl"></div>
			</div>
			<div class="flex flex-col gap-6 text-lg text-base-content/80 lg:w-1/2">
				<h2 class="text-4xl text-base-content">The Problem with "Top 10" Lists</h2>
				<p>
					It started with frustration. We found ourselves with 50 tabs open, jumping between Wikipedia articles, cluttered travel
					blogs, and map applications, just to answer a simple question: <em>"Is this building worth visiting?"</em>
				</p>
				<p>
					Most travel guides tell you where to go, but they rarely explain why it matters. They list attractions without context
					and viewpoints without history. We found the content dry, repetitive, and often focused on tourist traps rather than
					genuine quality.
				</p>
				<p class="font-bold text-base-content">
					We wanted the history behind the buildings. We wanted to know the history of the city.
				</p>
			</div>
		</div>
	</div>
{/snippet}

{#snippet Discovery()}
	<div class="flex flex-col gap-8">
		<div class="text-center">
			<h2 class="mb-4 text-4xl">The {SITE_NAME} Difference</h2>
			<p class="text-lg opacity-60">Everything you need to decide, plan, and learn in one view.</p>
		</div>

		<div class="grid grid-cols-1 gap-6 text-center md:grid-cols-3">
			{#each philosophies as item (item.title)}
				<SmallCard title={item.title} desc={item.desc} Icon={item.Icon} />
			{/each}
		</div>
	</div>
{/snippet}

{#snippet Disclaimer()}
	<p>
		Some descriptive content on this site is adapted from
		<a class="link" href="https://www.wikipedia.org" target="_blank" rel="noopener noreferrer">Wikipedia</a>
		and is used under the
		<a class="link" href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener noreferrer"
			>CC BY-SA 4.0 license</a
		>. Structured data (population, elevation, coordinates, etc.) is from
		<a class="link" href="https://www.wikidata.org" target="_blank" rel="noopener noreferrer">Wikidata</a>, released under
		<a class="link" href="https://creativecommons.org/publicdomain/zero/1.0/" target="_blank" rel="noopener noreferrer">CC0</a>.
	</p>
{/snippet}

{#snippet VisualManifesto()}
	<figure class="absolute inset-0 h-full w-full">
		<img
			class="h-full w-full object-cover"
			src={RomeImage}
			alt="Skyline of Rome, Italy"
			width="1641"
			height="547"
			draggable="false"
		/>
		<div class="absolute inset-0 bg-black/50 backdrop-grayscale"></div>
		<div class="absolute bottom-3 left-3">
			<Attribution
				attribution={{
					author: 'Nhartmannphotos',
					sourceUrl: 'https://en.wikipedia.org/wiki/File:Rome_skyline_panorama.jpg',
					licence: { name: 'CC BY-SA 4.0', link: 'https://creativecommons.org/licenses/by-sa/4.0/deed.en' }
				}}
			/>
		</div>
	</figure>

	<div class="relative z-10 max-w-4xl px-4 text-center text-white">
		<h2 class="mb-8 text-4xl italic md:text-5xl">
			"Beauty, History, and Utility.<br />All on one page."
		</h2>

		{const citiesNav = UrlUtil.toCities()}
		<a
			class="btn btn-wide rounded-full shadow-md btn-lg btn-primary"
			href="/{citiesNav.href}"
			data-sveltekit-preload-data={citiesNav.preloadData}
			data-sveltekit-preload-code={citiesNav.preloadCode}
		>
			Start Exploring
		</a>
	</div>
{/snippet}
